import { AccountViewRoute, config, Language } from '@backoffice-frontend/common'
import {
  AppearanceOptions,
  useAppearance,
} from '@backoffice-frontend/dark-mode'
import { isAdminPanel, isProduction } from '@backoffice-frontend/environment'
import { UserType } from '@backoffice-frontend/graphql'
import { Badge, Divider } from '@backoffice-frontend/patterns'
import { StatusAreaRoutes } from '@backoffice-frontend/status'
import { useTemporarySessionId } from '@backoffice-frontend/temporary-backoffice-sessions-common'
import { useBackofficeLoggedInUser } from '@backoffice-frontend/user-management-ui'
import { ChevronRightIcon, ExternalLinkIcon } from '@moia-dev/pace-icons'
import { ActionItem, Icon } from '@moia-dev/pace-web'
import { UpdatesAreaRoutes } from '@shared/updates'
import { motion } from 'framer-motion'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { LogoutButton } from '../../LogoutButton'
import { RefreshPermissionsButton } from '../../RefreshPermissionsButton'
import { MenuState, type MenuStateType } from './MenuState'

interface AccountBadgeMainMenuProps {
  setIsOpen: (isOpen: boolean) => void
  hasNotificationsUpdates: boolean
  hasNotificationsStatuses: boolean
  setUpdatesLastVisited: () => Promise<unknown>
  refetchUpdateLastVisit: () => Promise<unknown>
  setStatusLastVisited: () => Promise<unknown>
  refetchStatusLastVisit: () => Promise<unknown>
  setCurrentMenu: (menu: MenuStateType) => void
  groupSwitcherActionItem: ReactNode
}

const menuAnimations = {
  initial: { x: -50, opacity: 0 },
  animate: { x: 0, opacity: 1, transition: { duration: 0.1 } },
  exit: { x: -50, opacity: 0, transition: { duration: 0.1 } },
}

export const AccountBadgeMainMenu = ({
  setIsOpen,
  hasNotificationsUpdates,
  hasNotificationsStatuses,
  setUpdatesLastVisited,
  refetchUpdateLastVisit,
  setStatusLastVisited,
  refetchStatusLastVisit,
  setCurrentMenu,
  groupSwitcherActionItem,
}: AccountBadgeMainMenuProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { appearanceOption, darkMode } = useAppearance()
  const { user } = useBackofficeLoggedInUser()
  const isTemporarySession = !!useTemporarySessionId()

  const canAccessGroupSwitcherBackoffice =
    !isProduction() && user?.userType === UserType.Admin

  const canAccessGroupSwitcherAdminPanel =
    !isProduction() && isAdminPanel && user?.organizationId === 'moia-devco'

  return (
    <motion.div
      initial="initial"
      animate="animate"
      exit="exit"
      variants={menuAnimations}
    >
      {!isTemporarySession && (
        <ActionItem
          label={t('authenticatedAppView_account_button')}
          onClick={() => {
            setIsOpen(false)
            navigate(AccountViewRoute.route)
          }}
        />
      )}
      {!isAdminPanel && (
        <ActionItem
          label={t('authenticatedAppView_releaseNotes_button')}
          onClick={async () => {
            await setUpdatesLastVisited()
            await refetchUpdateLastVisit()
            navigate(UpdatesAreaRoutes.route)
            setIsOpen(false)
          }}
          trailing={
            hasNotificationsUpdates && (
              <Badge
                css={{
                  '& > span': {
                    backgroundColor: 'var(--color-accent-default)',
                    borderRadius: '4px',
                    color: 'var(--color-content-on-accent-default)',
                    fontSize: '12px',
                    height: '18px',
                    padding: '0 4px',
                    fontWeight: '400',
                    transform: 'none',
                    position: 'relative',
                  },
                }}
              />
            )
          }
        />
      )}
      <ActionItem
        label={t('authenticatedAppView_statusPage_button')}
        onClick={async () => {
          await setStatusLastVisited()
          await refetchStatusLastVisit()
          navigate(StatusAreaRoutes.route)
          setIsOpen(false)
        }}
        trailing={
          hasNotificationsStatuses && (
            <Badge
              css={{
                '& > span': {
                  backgroundColor: 'var(--color-accent-default)',
                  borderRadius: '4px',
                  color: 'var(--color-content-on-accent-default)',
                  fontSize: '12px',
                  height: '18px',
                  padding: '0 4px',
                  fontWeight: '400',
                  transform: 'none',
                  position: 'relative',
                },
              }}
            />
          )
        }
      />

      {(canAccessGroupSwitcherBackoffice || canAccessGroupSwitcherAdminPanel) &&
        groupSwitcherActionItem}
      <Divider spacing={1} />
      <ActionItem
        label={t('authenticatedAppView_languageSwitch_button', {
          language:
            config.getLanguage() === Language.de
              ? t('common_german_label')
              : t('common_english_label'),
        })}
        trailing={<ChevronRightIcon />}
        onClick={e => {
          e.stopPropagation()
          setCurrentMenu(MenuState.language)
        }}
      />
      <ActionItem
        label={t('authenticatedAppView_themeSwitch_button', {
          theme:
            appearanceOption === AppearanceOptions.system
              ? `${t('authenticatedAppView_appearanceOptionSystem')} ${
                  darkMode
                    ? t('authenticatedAppView_appearanceOptionDark')
                    : t('authenticatedAppView_appearanceOptionLight')
                }`
              : appearanceOption === AppearanceOptions.dark
                ? t('authenticatedAppView_appearanceOptionDark')
                : t('authenticatedAppView_appearanceOptionLight'),
        })}
        trailing={<ChevronRightIcon />}
        onClick={e => {
          e.stopPropagation()
          setCurrentMenu(MenuState.theme)
        }}
      />
      <ActionItem
        label={
          <span
            css={{
              display: 'flex',
              gap: 'var(--space-2)',
            }}
          >
            {t('authenticatedAppView_zendesk_button')}
            <Icon size={16}>
              <ExternalLinkIcon />
            </Icon>
          </span>
        }
        onClick={e => {
          e.stopPropagation()
          window.open('http://support.moia.io/', '_blank')
        }}
      />
      <Divider spacing={1} />
      <RefreshPermissionsButton />
      <LogoutButton />
    </motion.div>
  )
}
